import leftIcon from '@SvgIcon/chevron-left.svg'
import rightIcon from '@SvgIcon/chevron-right.svg'
import listIcon from '@SvgIcon/android-list.svg'
import calendarIcon from '@SvgIcon/calendar.svg'
import placeholderIcon from '@SvgIcon/../event-image-placeholder.svg'

import each from 'lodash/each'
import get from 'lodash/get'
import set from 'lodash/set'
import $ from 'jquery'
import moment from 'moment'

import svgIconHtml from '../../../shared/svgIconHtml'
import ListEvent from './ListEvent'
import EventInstanceModal from './EventInstanceModal'
import { transformRawEvent } from './transformRawEvent'

const EventName = {
  name: 'EventName',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  computed: {
    thumb() {
      if (!this.event.photo) {
        return null
      }

      const dir = this.event.photo.url.substring(0, this.event.photo.url.lastIndexOf('/'))

      return `${dir}/_thumb/${this.event.photo.filename}`
    },
  },

  template: `
    <div class="calendar__grid-day__event-info">
      <img class="event-thumb" v-if="thumb" :src="thumb" alt="">
      <span class="event-thumb" v-else>${placeholderIcon}</span>
      <div class="event-org-title-and-time">
        <div v-if="event.organization" class="event-organization" :title="event.organization">
          {{ event.organization | truncateWords(10) }}
        </div>
        <div class="event-title" :title="event.title">{{ event.title }}</div>
        <div v-if="event.timeStartMoment" class="event-time">
          {{ event.timeStartMoment.format('h:mma') }}
          <template v-if="event.timeEndMoment">
            – {{ event.timeEndMoment.format('h:mma') }}
          </template>
        </div>
      </div>
    </div>
  `,
}

export default {
  name: 'Calendar',

  components: {
    ListEvent,
    EventInstanceModal,
    EventName,
  },

  props: {
    eventsUrl: {
      type: String,
      required: true,
    },

    createUrl: {
      type: String,
      required: true,
    },

    initialDate: {
      type: String,
      required: true,
    },

    initialCategory: {
      type: String,
      required: true,
    },

    initialLocation: {
      type: String,
      required: true,
    },

    locations: {
      type: Array,
      required: true,
      default() {
        return []
      },
    },

    categories: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      mode: 'month', // Arts Catawba wants month as default
      date: moment(this.initialDate),
      filters: {
        location: this.initialLocation ? this.initialLocation : '*',
        category: this.initialCategory ? this.initialCategory : '*',
      },
      rawEvents: {},
      loadingEvents: false,
      loadedEventsOnce: false,
    }
  },

  computed: {
    computedCreateUrl() {
      return this.createUrl && this.createUrl.indexOf('404') === -1 ? this.createUrl : null
    },

    today() {
      return moment().set({
        hour: 0, minute: 0, second: 0, millisecond: 0,
      })
    },

    nextYear() {
      return this.today.clone().add(1, 'year')
    },

    startingDayOfWeek() {
      return 0 // Sunday
    },

    periodStart() {
      return moment({ year: this.date.year(), month: this.date.month(), day: 1 })
    },

    periodEnd() {
      return moment({ year: this.date.year(), month: this.date.month(), day: this.date.daysInMonth() })
    },

    lastMonthDay1() {
      return moment().subtract(1, 'months').startOf('month')
    },

    todayMonthDay1() {
      return moment({ year: this.today.year(), month: this.today.month(), day: 1 })
    },

    maxRangeStart() {
      return this.todayMonthDay1.add(12, 'months')
    },

    maxRangeEnd() {
      return moment({
        year: this.maxRangeStart.year(),
        month: this.maxRangeStart.month(),
        day: this.maxRangeStart.daysInMonth(),
      })
    },

    displayFirstDate() {
      return this.beginningOfWeek(this.periodStart, this.startingDayOfWeek)
    },

    displayLastDate() {
      return this.endOfWeek(this.periodEnd, this.startingDayOfWeek)
    },

    weeksOfPeriod() {
      const periodDayLength = Math.abs(this.displayFirstDate.diff(this.displayLastDate, 'days'))
      const numWeeks = Math.floor((periodDayLength + 1) / 7)

      return Array(numWeeks).fill(null).map((_, i) => this.displayFirstDate.clone().add(i * 7, 'days'))
    },

    allowGoPrevMonth() {
      return this.date.clone().subtract(1, 'months').isSameOrAfter(this.lastMonthDay1, 'month')
    },

    allowGoNextMonth() {
      return this.date.clone().add(1, 'months').isSameOrBefore(this.maxRangeStart, 'month')
    },

    events() {
      if (this.isListMode) {
        return this.rawEvents
      }

      if (this.isMonthMode) {
        const eventsObject = {}

        each(this.rawEvents, (event) => {
          const eventEnd = moment(event.dateEnd)
          const tempDate = moment(event.dateStart)

          while (tempDate.isSameOrBefore(eventEnd)) {
            const date = tempDate.format('YYYY-MM-DD')
            if (!(date in eventsObject)) {
              eventsObject[date] = []
            }

            eventsObject[date].push(event)
            tempDate.add({ days: 1 })
          }
        })

        return eventsObject
      }

      return []
    },

    filteredEvents() {
      if (this.isListMode) {
        return this.events.filter((event) => moment(event.dateEnd).isSameOrAfter(this.today))
      }

      if (this.isMonthMode) {
        return this.events
      }

      return []
    },

    groupedEvents() {
      if (!this.isListMode) return []

      // Group events for a more condensed display.
      const events = new Map()

      this.filteredEvents.forEach((event) => {
        const key = `${event.id}-${event.timeStart}-${event.timeEnd}`
        if (events.has(key)) {
          const prevInstance = events.get(key)
          if (event.dateEndMoment.isSame(prevInstance.dateEndMoment.clone().add(1, 'day'))) {
            prevInstance.dateEnd = event.dateEnd
            prevInstance.dateEndMoment = event.dateEndMoment
          }
        } else {
          events.set(key, { ...event })
        }
      })

      return events.values()
    },

    isListMode() {
      return this.mode === 'list'
    },

    isMonthMode() {
      return this.mode === 'month'
    },
  },

  watch: {
    mode() {
      this.refreshEvents()
    },

    filters: {
      deep: true,
      handler() {
        this.refreshEvents()
      },
    },
  },

  methods: {
    goPrevMonth() {
      this.date = this.date.clone().subtract(1, 'months')
      this.refreshEvents()
    },

    goNextMonth() {
      this.date = this.date.clone().add(1, 'months')
      this.refreshEvents()
    },

    beginningOfWeek(date, startDow) {
      return date.clone().add((startDow - date.day() - 7) % -7, 'days')
    },

    endOfWeek(date, startDow) {
      return this.beginningOfWeek(date, startDow).add(7, 'days')
    },

    daysOfWeek(weekStart) {
      return Array(7).fill(null).map((_, i) => weekStart.clone().add(i, 'days'))
    },

    isCurrentDay(date) {
      return date.isSame(this.today, 'day')
    },

    isHistory(date) {
      return date < this.today
    },

    isSameMonth(date1, date2) {
      return date1.month() === date2.month()
    },

    eventsOfDate(date) {
      const ymd = date.format('YYYY-MM-DD')

      if (!(ymd in this.filteredEvents)) {
        return []
      }

      return this.filteredEvents[ymd]
    },

    getCachedEvents(filters, range) {
      return get(this.cache, [JSON.stringify(filters), range], null)
    },

    cacheEvents(filters, range, events) {
      return set(this.cache, [JSON.stringify(filters), range], events)
    },

    refreshEvents() {
      if (this.eventsXhr) {
        this.eventsXhr.abort()
      }

      this.loadingEvents = false
      clearTimeout(this.eventsLoadingTimer)

      const rangeString = `${this.today.format('YYYY-MM-DD')}-${this.maxRangeEnd.format('YYYY-MM-DD')}`

      const cachedEvents = this.getCachedEvents(this.filters, rangeString)
      if (cachedEvents) {
        this.rawEvents = cachedEvents
        return
      }

      this.rawEvents = []

      this.eventsLoadingTimer = setTimeout(() => {
        this.loadingEvents = true
      }, 300)

      this.eventsXhr = $.ajax({
        type: 'GET',
        url: this.eventsUrl,
        data: {
          mode: this.mode,
          start: this.lastMonthDay1.format('YYYY-MM-DD'),
          end: this.maxRangeEnd.format('YYYY-MM-DD'),
          filters: this.filters,
        },
        dataType: 'json',
        success: (events) => {
          this.loadedEventsOnce = true
          this.rawEvents = events.map(transformRawEvent)
          this.cacheEvents(this.filters, rangeString, this.rawEvents)
        },
      })
        .always(() => {
          this.loadingEvents = false
          clearTimeout(this.eventsLoadingTimer)
        })
    },

    showEvent(event) {
      this.$refs.modal.show(event.title, event.dataUrl)
    },

    dateToInt(dateString) {
      return parseInt(dateString.replace(/-/g, ''), 10)
    },

    eventHasDetails(event) {
      return event.organization
        || event.hasTextDescription
        || event.hasRichDescription
        || event.contactName
        || event.contactPhone
        || event.contactEmail
        || event.location
        || event.address
        || event.county
        || event.admission
        || event.url
    },
  },

  created() {
    this.cache = {}
    this.eventsXhr = null
    this.eventsLoadingTimer = null
    this.refreshEvents()

    // const handleSmallScreen = (mql) => {
    //   if (mql.matches) {
    //     this.mode = 'month'
    //   }
    // }
    //
    // window.APP.mq.small.addEventListener('change', handleSmallScreen)
    // handleSmallScreen(window.APP.mq.small)
  },

  // language=HTML (for PhpStorm)
  template: `
    <div class="calendar">
      <div class="calendar__header">
        <div class="calendar__date-and-nav" v-if="isMonthMode">
          <h2 class="calendar__date">{{ date.format('MMMM YYYY') }}</h2>
          <div class="calendar__nav" v-if="isMonthMode">
            <button type="button" class="small button" aria-label="Prev month"
              @click="goPrevMonth"
              :disabled="!allowGoPrevMonth"
            >
              ${svgIconHtml(leftIcon)}
            </button>
            <button type="button" class="small button" aria-label="Next month"
              @click="goNextMonth"
              :disabled="!allowGoNextMonth"
            >
              ${svgIconHtml(rightIcon)}
            </button>
          </div>
        </div>
        <div class="calendar__filters">
          <select v-model="filters.category" aria-label="Show by category">
            <option value="*">All Categories</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
          </select>
          <select v-model="filters.location" aria-label="Show by location">
            <option value="*">All Locations</option>
            <option v-for="location in locations" :key="location" :value="location">{{ location }}</option>
          </select>
        </div>
        <div class="calendar__actions">
          <button type="button" class="small secondary button"
            @click="mode = 'list'"
            :class="{ selected: isListMode }" aria-label="View as list">
            ${svgIconHtml(listIcon)}
            List
          </button>
          <button type="button" class="small secondary button"
            @click="mode = 'month'"
            :class="{ selected: isMonthMode }" aria-label="View as calendar">
            ${svgIconHtml(calendarIcon)}
            Month
          </button>
          <a v-if="computedCreateUrl" :href="computedCreateUrl" class="small primary button">Add an Event</a>
        </div>
      </div>
      <div class="calendar__body" :class="{ 'grid-mode': isMonthMode, 'list-mode': isListMode }">
        <div class="loading" v-if="loadingEvents">
          <ui-progress-circular :size="72" :indeterminate="true" />
        </div>

        <table v-if="isMonthMode" class="calendar__grid">
          <thead>
            <tr>
              <th scope="col">Sun</th>
              <th scope="col">Mon</th>
              <th scope="col">Tue</th>
              <th scope="col">Wed</th>
              <th scope="col">Thu</th>
              <th scope="col">Fri</th>
              <th scope="col">Sat</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(weekStart, weekIdx) in weeksOfPeriod" :key="weekIdx">
              <td v-for="(day, dayIdx) in daysOfWeek(weekStart)" :key="dayIdx"
                class="calendar__grid-day"
                :class="{ 
                    'current-day': isCurrentDay(day),
                    'outside-of-month': !isSameMonth(day, date),
                    'is-history': isHistory(day),
                }"
              >
                <div class="calendar__grid-day__num"><span>{{ day.date() }}</span></div>
                <template v-for="(event, eventIdx) in eventsOfDate(day)">
                  <button
                    v-if="eventHasDetails(event)"
                    :key="eventIdx"
                    class="calendar__grid-day__event has-details"
                    :class="{ 'is-cancelled': event.cancelled }"
                    @click="showEvent(event)"
                  >
                    <EventName :event="event" />
                  </button>
                  <div
                    v-else
                    :key="eventIdx"
                    class="calendar__grid-day__event is-without-details"
                    :class="{ 'is-cancelled': event.cancelled }"
                  >
                    <EventName :event="event" />
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="isListMode" class="container">
          <div class="loading" v-if="loadingEvents">
            <ui-progress-circular :size="72" :indeterminate="true" />
          </div>
          <div v-else class="calendar__list">
            <template v-if="!loadedEventsOnce">
              Loading...
            </template>
            <template v-else>
              <p v-if="!events.length">
                No events found.
              </p>
              <ListEvent
                v-else
                v-for="event in groupedEvents"
                :key="event.instanceId"
                :initial-event="event"
              />
            </template>
          </div>
        </div>
      </div>

      <EventInstanceModal ref="modal" />
    </div>
  `,
}
